import React, { useRef, useState } from 'react';
import { TIER_STYLES } from '../constants';
import TierLegendPopover, { TierLegendButton } from './TierLegendPopover';

// Hover tooltip for the confidence label — explains what each value means
// without burning a popover slot.
const CONFIDENCE_TOOLTIPS = {
    'Document-Verified': 'Every contributing variable came from a High-Trust document. No cap — score can reach 90-100 (Highest Confidence).',
    'Mixed':             'Combination of provenance types — some variables doc-verified, some proxy, some self-selected. If any are doc-verified the 90-100 cap is removed; otherwise capped at 89.',
    'Self-Selected':     'Every contributing variable was a manual rating choice. Capped at 89 (Watch) regardless of how favorable the ratings are.',
    'Proxy':             'Every contributing variable came from public-data baselines (HUD, Census, NCSHA). Capped at 89 (Watch).',
};

function provenanceBreakdown(impacts) {
    if (!Array.isArray(impacts) || impacts.length === 0) return null;
    const counts = impacts.reduce((acc, i) => {
        if (!i?.provenance) return acc;
        acc[i.provenance] = (acc[i.provenance] || 0) + 1;
        return acc;
    }, {});
    const parts = [];
    if (counts.doc_verified)  parts.push(`${counts.doc_verified} doc-verified`);
    if (counts.proxy)         parts.push(`${counts.proxy} proxy`);
    if (counts.self_selected) parts.push(`${counts.self_selected} self-selected`);
    return parts.length ? parts.join(' · ') : null;
}

// Compact label shown inside the tier pill — "Highest Confidence" is too wide
// for a narrow score card. The popover still uses the full tier name.
const TIER_SHORT_LABEL = {
    'Highest Confidence': 'Highest',
};

export default function ScoreCard({ scores, tier, confidence, cappedByConfidenceGate, criticalOverride, impacts, stale = false }) {
    const tierStyle = TIER_STYLES[tier] || TIER_STYLES['Watch'];
    const [showLegend, setShowLegend] = useState(false);
    const legendBtnRef = useRef(null);
    const shortTier = TIER_SHORT_LABEL[tier] || tier;

    return (
        <div className={`group relative rounded-2xl border ${stale ? 'border-blue-300 dark:border-blue-800' : 'border-slate-200/80 dark:border-slate-700/70'} bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-950 p-6 shadow-lg shadow-slate-200/60 dark:shadow-black/40 ring-1 ring-inset ${tierStyle.ring} overflow-hidden`}>
            {/* Tier-tinted ambient glow gives the card depth without color-blocking it. */}
            <div aria-hidden className={`pointer-events-none absolute -top-20 -right-16 h-48 w-48 rounded-full ${tierStyle.bg} opacity-[0.18] blur-3xl`} />
            {/* Hairline sheen along the top edge. */}
            <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent dark:via-white/10" />

            <div className="relative">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 dark:text-slate-400">JPRO Index</div>
                        <div className="mt-1.5 flex items-baseline gap-2">
                            <div className={`text-6xl font-bold leading-none tracking-tight tabular-nums ${stale ? 'text-blue-600 dark:text-blue-400' : 'text-slate-900 dark:text-white'}`}>
                                {scores?.jpro ?? '—'}
                                {stale && <span className="text-3xl align-top ml-0.5" title="This baseline is out of date — Calculate to refresh">*</span>}
                            </div>
                            <div className="text-sm font-medium text-slate-400 dark:text-slate-500">/ 100</div>
                        </div>
                        {stale && (
                            <div className="mt-1 text-xs text-blue-600 dark:text-blue-400 font-medium">
                                Inputs changed — score will update on Calculate
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        <div title={tier || 'Pending'}
                             className={`${tierStyle.bg} ${tierStyle.fg} rounded-lg px-2.5 py-1.5 text-xs font-semibold whitespace-nowrap shadow-sm`}>
                            {shortTier || 'Pending'}
                        </div>
                        <TierLegendButton buttonRef={legendBtnRef} onClick={() => setShowLegend(v => !v)} />
                        <TierLegendPopover currentTier={tier} anchorRef={legendBtnRef}
                                           open={showLegend} onClose={() => setShowLegend(false)} />
                    </div>
                </div>

                {/* Overall score track. */}
                <ScoreTrack value={scores?.jpro} barClass={tierStyle.bg} className="mt-4" />

                <div className="grid grid-cols-2 gap-5 mt-6">
                    <SubScore label="Profitability" value={scores?.profitability}
                              textClass="text-blue-600 dark:text-blue-400" barClass="bg-blue-500" />
                    <SubScore label="Affordability" value={scores?.affordability}
                              textClass="text-green-600 dark:text-green-400" barClass="bg-green-500" />
                </div>

                <div className="mt-5 pt-4 border-t border-slate-200/80 dark:border-slate-700/70 flex flex-wrap items-center gap-2 text-xs">
                <span className="text-slate-500 dark:text-slate-400">Confidence:</span>
                <span title={CONFIDENCE_TOOLTIPS[confidence] || ''}
                      className="font-medium text-slate-800 dark:text-slate-100 underline decoration-dotted decoration-slate-400 underline-offset-2 cursor-help">
                    {confidence}
                </span>
                {(() => {
                    const breakdown = provenanceBreakdown(impacts);
                    return breakdown ? (
                        <span className="text-slate-500 dark:text-slate-400 tracking-tight">
                            ({breakdown})
                        </span>
                    ) : null;
                })()}
                {cappedByConfidenceGate && (
                    <span className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-200 font-medium">
                        Capped at 89 — upload High-Trust docs to reach 90+
                    </span>
                )}
                {criticalOverride?.triggered && (
                    <span className="px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-200 font-medium">
                        Critical Override
                    </span>
                )}
                </div>
            </div>
        </div>
    );
}

// Slim progress track for the overall JPRO index. Width animates on mount/update.
function ScoreTrack({ value, barClass, className = '' }) {
    const pct = Math.max(0, Math.min(100, Number(value) || 0));
    return (
        <div className={`h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800 ${className}`}>
            <div className={`h-full rounded-full ${barClass} transition-[width] duration-700 ease-out`}
                 style={{ width: `${pct}%` }} />
        </div>
    );
}

// Sub-score block: label, value, and a thin colored bar for at-a-glance comparison.
function SubScore({ label, value, textClass, barClass }) {
    const has = value !== null && value !== undefined && value !== '';
    const pct = Math.max(0, Math.min(100, Number(value) || 0));
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</div>
            <div className="mt-0.5 flex items-baseline gap-1">
                <span className={`text-2xl font-semibold tabular-nums ${textClass}`}>{has ? value : '—'}</span>
                <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500">/ 100</span>
            </div>
            <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <div className={`h-full rounded-full ${barClass} transition-[width] duration-700 ease-out`}
                     style={{ width: has ? `${pct}%` : '0%' }} />
            </div>
        </div>
    );
}
